import { useSelector } from 'react-redux';
import { StoreInterface } from '../../../store/types';
import { AddressElement, PaymentElement } from '@stripe/react-stripe-js';
import useOrderFlow from '../../../hooks/order/useOrderFlow';
import AddressHelpers from '../../../services/helpers/AddressHelpers';
import { PaymentMethodProps } from './PaymentMethod';
import usePaymentFlow from '../../../hooks/payment/usePaymentFlow';
import React, { useMemo } from 'react';
import useAuth from '../../../hooks/global/useAuth';
import usePaymentCoordinator from '../../../hooks/payment/usePaymentCoordinator';
import SavePaymentInfoConsent from './SavePaymentInfoConsent';
import AttachablePaymentMethod from './AttachablePaymentMethod';

export default function CardPaymentMethod(props: PaymentMethodProps) {
  const { id } = props;

  const { company } = useSelector((state: StoreInterface) => state.initialData);
  const { profile } = useSelector((state: StoreInterface) => state.profile);

  const { isGuest } = useAuth();
  const { data } = useOrderFlow();
  const { loading } = usePaymentCoordinator();
  const { loading: blocked, selectedPaymentMethod, paymentOptions, findSavedPaymentMethod } = usePaymentFlow();

  const savedCard = useMemo(() => findSavedPaymentMethod(id), [paymentOptions]);

  const addressRequired = company?.country !== 'DE';

  return (
    <AttachablePaymentMethod {...props}>
      {selectedPaymentMethod === id && !savedCard && !loading && !blocked && (
        <div className="p-sm border bg-[#F5F5F5] dark:bg-black">
          <PaymentElement
            options={{
              wallets: {
                applePay: 'never',
                googlePay: 'never',
              },
              fields: {
                billingDetails: {
                  email: 'auto',
                  name: 'auto',
                  phone: 'auto',
                  address: 'auto',
                },
              },
              defaultValues: {
                billingDetails: {
                  email: profile?.email ?? data.customer_email,
                  name: `${profile?.first_name ?? data.customer_first_name} ${profile?.last_name ?? data.customer_last_name}`,
                  phone: profile?.phone_number ?? data.customer_phone_number,
                  address: {
                    country: profile?.country ?? data.delivery_country,
                    postal_code: profile?.zip_code ?? data.delivery_zip_code,
                    state: profile?.state ?? data.delivery_state,
                    city: profile?.city ?? data.delivery_city,
                    line1:
                      profile?.address ??
                      AddressHelpers.setCountry(data.delivery_country).format({
                        street_name: data.delivery_street_name,
                        street_number: data.delivery_street_number,
                      }),
                  },
                },
              },
            }}
          />
          {addressRequired && (
            <AddressElement
              className="mt-xs"
              options={{
                mode: 'billing',
                fields: {
                  phone: 'never',
                },
                defaultValues: {
                  name: `${profile?.first_name ?? data.customer_first_name} ${profile?.last_name ?? data.customer_last_name}`,
                  address: {
                    country: profile?.country ?? data.delivery_country,
                    postal_code: profile?.zip_code ?? data.delivery_zip_code,
                    state: profile?.state ?? data.delivery_state,
                    city: profile?.city ?? data.delivery_city,
                    line1:
                      profile?.address ??
                      AddressHelpers.setCountry(data.delivery_country).format({
                        street_name: data.delivery_street_name,
                        street_number: data.delivery_street_number,
                      }),
                  },
                },
              }}
            />
          )}
          {!isGuest && <SavePaymentInfoConsent className="mt-sm" />}
        </div>
      )}
    </AttachablePaymentMethod>
  );
}
